/** Shared vitals field definitions, types, and helpers used across the clinical UI. */

import {
  celsiusToFahrenheit,
  fahrenheitToCelsius,
  type PatchVitalsBody,
  type RegionId,
  requireRegion,
  type Vitals,
} from '@snootsvet/shared'

export type VitalsValues = {
  weight: number | null
  temperature: number | null
  heartRate: number | null
  respiratoryRate: number | null
  bodyConditionScore: number | null
  painScore: number | null
}

export const DEFAULT_VITALS: VitalsValues = {
  weight: null,
  temperature: null,
  heartRate: null,
  respiratoryRate: null,
  bodyConditionScore: null,
  painScore: null,
}

type VitalsField = { key: keyof VitalsValues; label: string; unit: string }

/**
 * Returns the ordered vitals input fields for a region. Weight is always entered
 * in kg; only temperature's entry unit varies by region (°F in the US, else °C).
 */
export function getVitalsFields(regionId: RegionId): VitalsField[] {
  const temperatureUnit = requireRegion(regionId).temperatureUnit === 'F' ? '°F' : '°C'
  return [
    { key: 'weight', label: 'Weight', unit: 'kg' },
    { key: 'temperature', label: 'Temp', unit: temperatureUnit },
    { key: 'heartRate', label: 'Heart Rate', unit: 'bpm' },
    { key: 'respiratoryRate', label: 'Resp. Rate', unit: 'bpm' },
    { key: 'bodyConditionScore', label: 'BCS', unit: '1–9' },
    { key: 'painScore', label: 'Pain Score', unit: '0–10' },
  ]
}

/**
 * Maps local numeric input state to the vitals API shape, used for both POST
 * (create) and PATCH (update). Empty fields are sent as explicit `null` rather
 * than omitted, so that clearing a value in the edit form clears it on the
 * stored record; on create, null is equivalent to omitting. Weight is passed
 * through as kg; temperature is converted from the region's input unit (°F in
 * the US) back to canonical °C before saving.
 */
export function vitalsToApi(v: VitalsValues, regionId: RegionId): PatchVitalsBody {
  const region = requireRegion(regionId)
  const isConvertedFromFahrenheit = v.temperature != null && region.temperatureUnit === 'F'
  const temperatureC = isConvertedFromFahrenheit
    ? fahrenheitToCelsius(v.temperature as number)
    : v.temperature
  return {
    weight: v.weight != null ? String(v.weight) : null,
    // Round the °F->°C conversion to 2dp to match the numeric(5,2) column and
    // keep the payload symmetric with vitalsFromServer's rounding on the way out.
    temperature:
      temperatureC != null
        ? isConvertedFromFahrenheit
          ? temperatureC.toFixed(2)
          : String(temperatureC)
        : null,
    heartRate: v.heartRate,
    respiratoryRate: v.respiratoryRate,
    bodyConditionScore: v.bodyConditionScore,
    painScore: v.painScore,
  }
}

/** True when at least one vital measurement has been entered. */
export function hasAnyVital(v: VitalsValues): boolean {
  return Object.values(v).some((value) => value != null)
}

/**
 * Maps a server Vitals row (canonical kg / °C, serialized as strings) to local
 * numeric input state. Temperature is converted to the region's input unit (°F
 * in the US), rounded to one decimal to keep the input tidy.
 */
export function vitalsFromServer(
  v: Pick<
    Vitals,
    'weight' | 'temperature' | 'heartRate' | 'respiratoryRate' | 'bodyConditionScore' | 'painScore'
  >,
  regionId: RegionId
): VitalsValues {
  const region = requireRegion(regionId)
  const tempC = v.temperature != null ? Number(v.temperature) : null
  const temperature =
    tempC != null && region.temperatureUnit === 'F'
      ? Number(celsiusToFahrenheit(tempC).toFixed(1))
      : tempC
  return {
    weight: v.weight != null ? Number(v.weight) : null,
    temperature,
    heartRate: v.heartRate ?? null,
    respiratoryRate: v.respiratoryRate ?? null,
    bodyConditionScore: v.bodyConditionScore ?? null,
    painScore: v.painScore ?? null,
  }
}
